import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * Timed "join the waitlist" popup.
 *
 * Extends the theme dialog so it inherits scroll locking, click-outside, escape
 * handling and the shared open/close animation. Adds:
 * - a delay that only counts time the tab is actually visible;
 * - a per-visitor frequency cap (localStorage, calendar days) plus a same-session
 *   cap so closing it never brings it back that day / that visit;
 * - an async submit so signing up never reloads the page out from under the popup.
 */
class WaitlistPopup extends DialogComponent {
  /** @type {number | null} */
  #timer = null;

  /** @type {number} */
  #visibleMs = 0;

  /** @type {number} */
  #lastTick = 0;

  /** @type {boolean} */
  #opened = false;

  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.querySelector('form');
    // Disable native tooltip so our styled inline error is the single source of truth.
    // This also guarantees handleSubmit always fires, so we can gate #showSuccess.
    this.#form?.setAttribute('novalidate', '');
    this.#form?.addEventListener('submit', this.handleSubmit);
    this.querySelector('input[type="email"]')?.addEventListener('input', this.#handleEmailInput);

    // In the theme editor the popup follows section selection instead of the timer,
    // otherwise it would fight the merchant while they work.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
      return;
    }

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDismiss);

    // A native form post (the fetch fallback, possibly via Shopify's CAPTCHA
    // challenge) returns here with ?waitlist_popup=success — greet with the
    // thank-you state instead of silently dropping the signup confirmation.
    this.#showSuccessIfReturned();

    if (!this.#canShow()) return;

    this.#lastTick = performance.now();
    this.#timer = window.setInterval(this.#tick, 500);
  }

  #handleEmailInput = () => {
    const email = this.querySelector('input[type="email"]');
    const error = this.querySelector('[data-waitlist-error]');
    if (error instanceof HTMLElement) error.hidden = true;
    if (email instanceof HTMLInputElement) email.removeAttribute('aria-invalid');
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stopTimer();
    this.#form?.removeEventListener('submit', this.handleSubmit);
    this.querySelector('input[type="email"]')?.removeEventListener('input', this.#handleEmailInput);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDismiss);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
  }

  #stopTimer() {
    if (this.#timer === null) return;
    window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #tick = () => {
    const now = performance.now();

    // A hidden tab shouldn't burn down the delay — reset the baseline and wait.
    if (document.hidden) {
      this.#lastTick = now;
      return;
    }

    // Neither should an open drawer (cart/checkout, search, menu) — never
    // interrupt a customer who is trying to complete a purchase.
    if (document.querySelector('theme-drawer[open]')) {
      this.#lastTick = now;
      return;
    }

    this.#visibleMs += now - this.#lastTick;
    this.#lastTick = now;

    if (this.#visibleMs < this.#delay) return;

    this.#stopTimer();
    this.#reveal();
  };

  #reveal() {
    if (this.#opened || !this.#canShow()) return;

    this.#opened = true;
    this.#remember({ lastShown: Date.now() });
    this.showDialog();
  }

  /**
   * Closing (X, overlay, Escape, "Maybe later") parks the popup for the rest of
   * the local calendar day. Design-mode previews never write this.
   */
  #handleDismiss = () => {
    if (window.Shopify?.designMode) return;
    this.#remember({ lastShown: Date.now() });
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (event.target instanceof HTMLElement && event.target.contains(this)) this.showDialog();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (event.target instanceof HTMLElement && event.target.contains(this)) this.closeDialog();
  };

  /** Delay in milliseconds before the popup is allowed to open. */
  get #delay() {
    const delay = Number(this.dataset.delay);
    return Number.isFinite(delay) && delay > 0 ? delay : 60000;
  }

  get #storageKey() {
    return this.dataset.storageKey || 'ist1947:waitlist-popup';
  }

  get #sessionKey() {
    return `${this.#storageKey}:dismissed`;
  }

  /**
   * Whole local calendar days between a stored timestamp and today.
   * Closing at 11pm still counts as "today" — the popup can return after midnight,
   * not 24 hours later.
   *
   * @param {number} timestamp
   * @returns {number}
   */
  #calendarDaysSince(timestamp) {
    const last = new Date(timestamp);
    const now = new Date();
    const startLast = new Date(last.getFullYear(), last.getMonth(), last.getDate()).getTime();
    const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return Math.round((startNow - startLast) / 86400000);
  }

  /**
   * @returns {{ lastShown?: number, subscribed?: boolean }}
   */
  #recall() {
    try {
      const raw = window.localStorage.getItem(this.#storageKey);
      if (!raw) return {};

      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      // Storage blocked (private mode, cookies off) or corrupt — treat as a first visit.
      return {};
    }
  }

  /** @param {{ lastShown?: number, subscribed?: boolean }} patch */
  #remember(patch) {
    try {
      window.localStorage.setItem(this.#storageKey, JSON.stringify({ ...this.#recall(), ...patch }));
    } catch {
      // Nothing to do — the popup still works, it just can't cap itself across days.
    }

    if (patch.lastShown || patch.subscribed) {
      try {
        window.sessionStorage.setItem(this.#sessionKey, '1');
      } catch {
        // Same-session cap is best-effort.
      }
    }
  }

  #canShow() {
    const { lastShown, subscribed } = this.#recall();

    if (subscribed) return false;

    try {
      if (window.sessionStorage.getItem(this.#sessionKey) === '1') return false;
    } catch {
      // Ignore — localStorage still decides.
    }

    if (!lastShown) return true;

    const days = Number(this.dataset.frequencyDays);
    const minDays = Number.isFinite(days) && days > 0 ? days : 1;

    return this.#calendarDaysSince(lastShown) >= minDays;
  }

  /**
   * Posts the signup in the background so the popup can answer in place.
   * Falls back to a normal form post if fetch is unavailable or Shopify
   * answers with a CAPTCHA demand — that post (or the challenge it routes
   * through) returns to `?waitlist_popup=success`, which #showSuccessIfReturned
   * picks up on the next load.
   * Success ("Thank you") is ONLY shown after a valid email passes client
   * validation AND Shopify confirms the signup.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();

    const submit = form.querySelector('button[type="submit"]');
    const email = form.querySelector('input[type="email"]');
    const error = this.querySelector('[data-waitlist-error]');

    if (error instanceof HTMLElement) error.hidden = true;
    if (email instanceof HTMLInputElement) email.removeAttribute('aria-invalid');

    // --- Client validation: success state must never appear without a real email ---
    const emailValue = email instanceof HTMLInputElement ? email.value.trim() : '';

    if (!(email instanceof HTMLInputElement) || emailValue === '') {
      this.#showError(error, submit, email, 'Please enter your email address.');
      return;
    }

    // Use both native validation and a strict pattern — `type="email"` alone is lenient.
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = email.checkValidity() && emailPattern.test(emailValue);

    if (!isValidEmail) {
      this.#showError(error, submit, email, 'Please enter a valid email address.');
      return;
    }

    if (submit instanceof HTMLButtonElement) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      const label = submit.querySelector('span');
      if (label) label.dataset.prevText = label.textContent || '';
      if (label) label.textContent = 'Joining…';
      submit.style.pointerEvents = 'none';
    }
    if (email instanceof HTMLInputElement) email.disabled = true;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? window.setTimeout(() => controller.abort(), 8000) : null;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        signal: controller ? controller.signal : undefined,
      });
      if (timeoutId) window.clearTimeout(timeoutId);

      const url = response.url || '';
      const posted =
        url.includes('waitlist_popup=success') ||
        url.includes('customer_posted=true') ||
        url.includes('contact_posted=true');

      // Shopify only redirects (to `?customer_posted=true`) when the signup was accepted.
      if (response.ok && (response.redirected || posted)) {
        this.#remember({ subscribed: true, lastShown: Date.now() });
        this.#showSuccess();
        return;
      }

      // 200 without a redirect means Shopify re-rendered the page with a form error.
      // Try to surface a real Shopify error if present, otherwise use generic copy.
      if (response.ok) {
        let message = 'That email could not be saved. Please check it and try again.';
        try {
          const html = await response.text();
          const match = html.match(/<[^>]*form[^>]*>([\s\S]*?)<\/form>/i);
          const snippet = match ? match[1] : html;
          // Look for common Shopify error markers in the returned HTML.
          if (/already.*subscribed|already.*exists|has already been taken/i.test(snippet)) {
            message = 'This email is already on the list.';
          } else if (/invalid.*email|email.*invalid/i.test(snippet)) {
            message = 'Please enter a valid email address.';
          }
        } catch {
          // ignore parse errors — use generic message
        }
        this.#showError(error, submit, email, message);
        return;
      }

      form.submit();
    } catch (err) {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (err && err.name === 'AbortError') {
        this.#showError(error, submit, email, 'Taking a bit long — please try again.');
        return;
      }
      form.submit();
    }
  };

  /**
   * Opens straight into the thank-you state when the page loads back from a
   * native signup post (`?waitlist_popup=success`, or Shopify's own
   * `customer_posted=true`). Also tidies the URL so a refresh doesn't replay it.
   */
  #showSuccessIfReturned() {
    const params = new URLSearchParams(window.location.search);
    const returned =
      params.get('waitlist_popup') === 'success' ||
      params.get('customer_posted') === 'true';

    if (!returned) return;

    // Open after load + settle so section hydration (which re-renders sections
    // on idle and can wipe the dialog's open attribute) has finished first.
    const openWhenSettled = () => window.setTimeout(() => {
      // The signup was accepted server-side, so this visitor is on the list —
      // the popup should never nag them again.
      this.#remember({ subscribed: true, lastShown: Date.now() });
      this.#opened = true;
      this.#showSuccess();

      params.delete('waitlist_popup');
      params.delete('customer_posted');
      const query = params.toString();
      window.history.replaceState(null, '', query ? `?${query}` : window.location.pathname);
    }, 1200);

    if (document.readyState === 'complete') {
      openWhenSettled();
    } else {
      window.addEventListener('load', openWhenSettled, { once: true });
    }
  }

  /**
   * @param {Element | null} error
   * @param {Element | null} submit
   * @param {Element | null} email
   * @param {string} [message]
   */
  #showError(error, submit, email, message) {
    if (error instanceof HTMLElement) {
      error.textContent = message || 'That email could not be saved. Please check it and try again.';
      error.hidden = false;
    }
    if (email instanceof HTMLInputElement) {
      email.setAttribute('aria-invalid', 'true');
      email.disabled = false;
      email.focus();
    }
    if (submit instanceof HTMLButtonElement) {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
      submit.style.pointerEvents = '';
      const label = submit.querySelector('span');
      if (label && label.dataset.prevText) label.textContent = label.dataset.prevText;
    }
  }

  /** @param {number} [autoCloseMs] */
  #showSuccess(autoCloseMs = 4500) {
    const formState = this.querySelector('[data-waitlist-state="form"]');
    const successState = this.querySelector('[data-waitlist-state="success"]');

    if (formState instanceof HTMLElement) formState.hidden = true;
    if (successState instanceof HTMLElement) {
      successState.hidden = false;
      successState.setAttribute('tabindex', '-1');
      successState.focus();
    }

    this.showDialog();
    window.setTimeout(() => this.closeDialog(), autoCloseMs);
  }
}

if (!customElements.get('waitlist-popup')) {
  customElements.define('waitlist-popup', WaitlistPopup);
}
